/**
 * Subscription Reminder Dialog Component
 * Shows a popup reminder for subscribed companies when subscription is ending soon (≤3 days)
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { getSubscriptionStatus } from '../../services/companySubscriptionService';

/**
 * Subscription Reminder Dialog Component
 */
const SubscriptionReminderDialog = ({ open, onClose }) => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      loadSubscriptionStatus();
    }
  }, [open]);

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const data = await getSubscriptionStatus();
      setSubscriptionData(data);
    } catch (err) {
      console.error('Error loading subscription status:', err);
      // If error, close dialog
      if (onClose) {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open || loading || !subscriptionData) {
    return null;
  }

  const { active, daysRemaining, planName } = subscriptionData;

  // Only show if subscription is active and has days remaining
  if (!active || daysRemaining === null || daysRemaining === undefined) {
    return null;
  }

  // Only show if subscription ends in 3 days or less
  if (daysRemaining > 3) {
    return null;
  }

  const getMessage = () => {
    if (daysRemaining <= 0) {
      return 'Your subscription has ended. Please renew your subscription to continue using the service.';
    } else if (daysRemaining === 1) {
      return 'Your subscription will end in 1 day. Please renew your subscription to continue using the service.';
    } else {
      return `Your subscription will end in ${daysRemaining} days. Please renew your subscription to continue using the service.`;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Subscription Ending Soon
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            {getMessage()}
          </Typography>
          {planName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Current Plan: <strong>{planName}</strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Days Remaining: <strong>{daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}</strong>
          </Typography>
        </Alert>
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            To continue using all features after your subscription ends, please contact administration to renew your subscription.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="warning"
          fullWidth
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionReminderDialog;

